import React, { useEffect, useRef } from "react";
import * as THREE from "three";

const VERT = `
uniform float uTime;
varying float vElev;
varying float vDist;

float wave(vec2 p) {
  float e = 0.0;
  e += sin(p.x * 0.055 + uTime * 0.7) * 1.6;
  e += sin(p.y * 0.045 - uTime * 0.5) * 2.0;
  e += sin((p.x + p.y) * 0.032 + uTime * 0.35) * 2.4;
  e += sin(length(p) * 0.04 - uTime * 0.8) * 1.1;
  e += sin(p.x * 0.12 - uTime * 1.1) * sin(p.y * 0.1 + uTime * 0.9) * 0.9;
  return e;
}

void main() {
  vec3 pos = position;
  float e = wave(pos.xy);
  pos.z += e;
  vElev = e;
  vec4 mv = modelViewMatrix * vec4(pos, 1.0);
  vDist = -mv.z;
  gl_PointSize = clamp(150.0 / vDist, 1.0, 4.0);
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = `
varying float vElev;
varying float vDist;

void main() {
  vec2 c = gl_PointCoord - 0.5;
  if (dot(c, c) > 0.25) discard;
  vec3 green = vec3(0.0, 1.0, 0.61);
  vec3 cyan = vec3(0.0, 0.85, 1.0);
  vec3 col = mix(green, cyan, smoothstep(-3.0, 4.0, vElev));
  float fog = 1.0 - smoothstep(50.0, 280.0, vDist);
  float glow = 0.55 + 0.45 * smoothstep(-2.0, 5.0, vElev);
  gl_FragColor = vec4(col * glow, fog * 0.85);
}
`;

export default function TerrainBackground() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, powerPreference: "high-performance" });
    } catch (e) {
      return; // WebGL unavailable — silently skip the 3D background
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 600);
    camera.position.set(0, 16, 60);

    const geometry = new THREE.PlaneGeometry(700, 700, 170, 170);
    const material = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geometry, material);
    points.rotation.x = -Math.PI / 2;
    points.position.set(0, 0, -180);
    scene.add(points);

    const lookTarget = new THREE.Vector3(0, 2, -200);
    let scrollProgress = 0;
    let smooth = 0;

    const onScroll = () => {
      scrollProgress = Math.min(window.scrollY / (window.innerHeight * 1.6), 1);
    };
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    const clock = new THREE.Clock();
    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      material.uniforms.uTime.value = clock.getElapsedTime();
      smooth += (scrollProgress - smooth) * 0.06;
      // Scrolling tilts the camera down, closer to the terrain surface
      camera.position.y = 16 - 11.5 * smooth;
      camera.position.z = 60 - 14 * smooth;
      lookTarget.y = 2 - 6 * smooth;
      camera.lookAt(lookTarget);
      // Fade terrain as content sections take over
      renderer.domElement.style.opacity = String(1 - 0.72 * smooth);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div
      ref={mountRef}
      data-testid="terrain-background"
      className="fixed inset-0 z-0 pointer-events-none"
      aria-hidden="true"
    />
  );
}
